import { useNavigate } from 'react-router-dom'
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PayCircleOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { Flex, Tag, Tooltip, Typography } from 'antd'
import { useUserProfile } from 'src/features/user'
import { path } from 'src/libs/path'
import {
  openModalRequestsDelete,
  openModalRequestsApprove,
  openModalRequestView,
  openModalRequestsToSubmit,
  openModalRequestsRejectSeo,
  openModalRequestsReject,
} from 'src/pages/budget/budgetRequests/model'
import {
  useCurrencies,
  useCurrencyGate,
} from 'src/pages/budget/budgetRequests/hooks'
import { BudgetRequest } from 'src/pages/budget/budgetRequests/types'
import {
  formatAmount,
  formatUsdAmount,
  getUsdEquivalent,
} from 'src/pages/budget/budgetRequests/currency'
import { DescriptionCell } from 'src/pages/budget/components'
import {
  iconButtonStyle,
  PRODUCT_MAP,
  STATUS,
  STATUS_MAP_COLOR,
  STATUS_MAP_NAME,
  STATUS_PERIOD,
} from 'src/pages/budget/const'
import { useCeoTeam } from 'src/pages/budget/hook'
import { Product, TStatus, TStatusPeriod } from 'src/pages/budget/types'

type ColumnKey =
  | 'id'
  | 'articleName'
  | 'itemName'
  | 'description'
  | 'amount'
  | 'createdBy'
  | 'productIds'
  | 'status'
  | 'comment'
  | 'actionForApprove'
  | 'actionOwn'
  | 'actionOwnApprove'
  | 'actionOwnReject'
  | 'actionUrgent'

type RequestActionsProps = {
  record: BudgetRequest
  currentPeriodStatus?: TStatusPeriod
}

type RequestTeamActionsProps = RequestActionsProps & {
  isCeo: boolean
}

const canCreatePaymentRequest = (record: BudgetRequest) => {
  const isApproved = record.status === STATUS.APPROVED
  // return (
  //   isApproved &&
  //   (currentPeriodStatus === STATUS_PERIOD.OPEN_PAYMENT ||
  //     (record.isEarlyPayment && isNotClosedPeriodStatus))
  // )
  return false //todo
}

const RequestTeamActions = ({
  record,
  currentPeriodStatus,
  isCeo,
}: RequestTeamActionsProps) => {
  const navigate = useNavigate()
  const isNotClosedPeriodStatus = currentPeriodStatus !== STATUS_PERIOD.CLOSED

  return (
    <Flex justify="flex-end" gap="large" style={{ fontSize: 20 }}>
      <Tooltip title="Просмотр заявки">
        <EyeOutlined
          onClick={() =>
            openModalRequestView({
              items: [record],
              mode: 'forApprove',
              periodStatus: currentPeriodStatus,
            })
          }
          style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
        />
      </Tooltip>
      {(record.status === STATUS.SUBMITTED ||
        (record.status === STATUS.IN_REVIEW &&
          record.availableApprovalSteps.length > 0)) &&
        isNotClosedPeriodStatus && (
          <Tooltip title="Редактировать заявку">
            <EditOutlined
              onClick={() =>
                navigate(path.budget.budgets.edit(String(record.id)))
              }
              style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
            />
          </Tooltip>
        )}
      {record.status === STATUS.APPROVED && isCeo && isNotClosedPeriodStatus && (
        <Tooltip title={`Отклонить заявку как SEO`}>
          <CloseOutlined
            onClick={() =>
              openModalRequestsRejectSeo({
                items: [record],
              })
            }
            style={{ ...iconButtonStyle, color: 'var(--color-red-60)' }}
          />
        </Tooltip>
      )}
      {(record.status === STATUS.SUBMITTED ||
        (record.status === STATUS.IN_REVIEW &&
          record.availableApprovalSteps.length > 0)) &&
        isNotClosedPeriodStatus && (
          <Tooltip title="Согласовать заявку">
            <CheckOutlined
              onClick={() =>
                openModalRequestsApprove({
                  items: [
                    {
                      id: record.id,
                      description: record.description,
                      availableApprovalSteps: record.availableApprovalSteps,
                    },
                  ],
                })
              }
              style={{
                ...iconButtonStyle,
                color: 'var(--crm-accent-green)',
              }}
            />
          </Tooltip>
        )}
      {(record.status === STATUS.SUBMITTED ||
        (record.status === STATUS.IN_REVIEW &&
          record.availableApprovalSteps.length > 0)) && (
        <Tooltip title="Отклонить заявку">
          <CloseOutlined
            onClick={() =>
              openModalRequestsReject({
                items: [
                  {
                    id: record.id,
                    description: record.description,
                    availableApprovalSteps: record.availableApprovalSteps,
                  },
                ],
              })
            }
            style={{ ...iconButtonStyle, color: 'var(--color-red-60)' }}
          />
        </Tooltip>
      )}
      {canCreatePaymentRequest(record) && (
        <Tooltip title={`Создать заявку на оплату`}>
          <PayCircleOutlined
            onClick={() =>
              navigate(
                path.budget.budgets.createPaymentRequest(String(record.id)),
              )
            }
            style={{ ...iconButtonStyle, color: 'var(--color-primary)' }}
          />
        </Tooltip>
      )}
    </Flex>
  )
}

const RequestOwnActions = ({
  record,
  currentPeriodStatus,
}: RequestActionsProps) => {
  const navigate = useNavigate()
  const isNotClosedPeriodStatus = currentPeriodStatus !== STATUS_PERIOD.CLOSED

  return (
    <Flex justify="flex-end" gap="large" style={{ fontSize: 20 }}>
      <Tooltip title="Просмотр заявки">
        <EyeOutlined
          onClick={() =>
            openModalRequestView({
              items: [record],
              mode: 'own',
              periodStatus: currentPeriodStatus,
            })
          }
          style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
        />
      </Tooltip>
      {(record.status === STATUS.DRAFT ||
        record.status === STATUS.SUBMITTED ||
        record.status === STATUS.REJECTED) &&
        isNotClosedPeriodStatus && (
          <Tooltip title="Редактировать заявку">
            <EditOutlined
              onClick={() =>
                navigate(path.budget.budgets.edit(String(record.id)))
              }
              style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
            />
          </Tooltip>
        )}
      {record.status === STATUS.DRAFT && isNotClosedPeriodStatus && (
        <Tooltip title="Удалить заявку">
          <DeleteOutlined
            onClick={() => openModalRequestsDelete({ items: [record] })}
            style={{ ...iconButtonStyle, color: 'var(--color-red-60)' }}
          />
        </Tooltip>
      )}
      {record.status === STATUS.DRAFT && isNotClosedPeriodStatus && (
        <Tooltip title="Отправить на согласование">
          <SendOutlined
            onClick={() => openModalRequestsToSubmit({ items: [record] })}
            style={{ ...iconButtonStyle, color: 'var(--cui-cyan)' }}
          />
        </Tooltip>
      )}
      {canCreatePaymentRequest(record) && (
        <Tooltip title={`Создать заявку на оплату`}>
          <PayCircleOutlined
            onClick={() =>
              navigate(
                path.budget.budgets.createPaymentRequest(String(record.id)),
              )
            }
            style={{ ...iconButtonStyle, color: 'var(--color-primary)' }}
          />
        </Tooltip>
      )}
    </Flex>
  )
}

export const getRequestListColumns = (
  columns: ColumnKey[],
  currentPeriodStatus?: TStatusPeriod,
  isApprovalList = false,
  hasRowSelection = false,
) => {
  const navigate = useNavigate()
  useCurrencyGate()
  const currencies = useCurrencies()
  const { Text } = Typography
  const userProfile = useUserProfile()
  const { ceoTeam } = useCeoTeam()
  const ARE_YOY_CEO = ceoTeam.includes(userProfile.id)
  const isNotClosedPeriodStatus = currentPeriodStatus !== STATUS_PERIOD.CLOSED

  const allColumns: Record<ColumnKey, any> = {
    id: {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },

    articleName: {
      title: 'Статья',
      key: 'articleName',
      render: (_: void, record: BudgetRequest) => (
        <DescriptionCell
          text={`${record.article.number}. ${record.article.name}`}
        />
      ),
    },

    itemName: {
      title: 'Пункт',
      key: 'article.name',
      render: (_: void, record: BudgetRequest) => (
        <DescriptionCell
          text={`${record.articleItem.fullNumber}  ${
            record.articleItem.name ?? ''
          }`}
        />
      ),
    },

    description: {
      title: 'Описание заявки',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => <DescriptionCell text={text} />,
    },

    amount: {
      title: 'Сумма',
      key: 'amount',
      render: (_: void, record: BudgetRequest) => {
        const usdEquivalent = getUsdEquivalent(
          record.amount,
          record.currency,
          currencies,
          record.approvedRate,
        )

        return (
          <Flex vertical gap={0}>
            <span style={{ fontSize: 16 }}>
              {formatAmount(record.amount, record.currency)}{' '}
              <Text type="secondary" style={{ fontSize: 16 }}>
                {record.currency}
              </Text>
            </span>
            {usdEquivalent && (
              <span style={{ fontSize: 14 }}>
                {formatUsdAmount(usdEquivalent)}{' '}
                <Text type="secondary" style={{ fontSize: 14 }}>
                  USD
                </Text>
              </span>
            )}
          </Flex>
        )
      },
    },

    createdBy: {
      title: 'Заявитель',
      key: 'createdBy.fullname',
      render: (_: void, record: BudgetRequest) => (
        <>{record.createdBy.fullname}</>
      ),
    },

    productIds: {
      title: 'Продукт',
      align: 'center',
      dataIndex: 'productIds',
      key: 'productIds',
      render: (value: Product[] | null) => (
        <Flex gap={5} wrap justify="center">
          {value && value.length > 0 ? (
            value.map((product) => (
              <Tag key={product}>{PRODUCT_MAP[product]} </Tag>
            ))
          ) : (
            <strong>-</strong>
          )}
        </Flex>
      ),
    },

    status: {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: TStatus) => (
        <Tag color={STATUS_MAP_COLOR[status] ?? ''} style={{ fontSize: 14 }}>
          {isApprovalList && status === STATUS.SUBMITTED
            ? 'Ждет согласования'
            : STATUS_MAP_NAME[status] ?? status}
        </Tag>
      ),
    },

    comment: {
      title: 'Причина отклонения',
      dataIndex: 'comment',
      key: 'comment',
    },

    actionUrgent: {
      title: '',
      key: 'action',
      width: 194,
      render: (_: void, record: BudgetRequest) => {
        const isOwnRequest = userProfile.id === record.createdBy.id
        return isOwnRequest ? (
          <RequestOwnActions
            record={record}
            currentPeriodStatus={currentPeriodStatus}
          />
        ) : (
          <RequestTeamActions
            record={record}
            currentPeriodStatus={currentPeriodStatus}
            isCeo={ARE_YOY_CEO}
          />
        )
      },
    },

    actionForApprove: {
      title: '',
      key: 'action',
      width: 194,
      render: (_: void, record: BudgetRequest) => (
        <RequestTeamActions
          record={record}
          currentPeriodStatus={currentPeriodStatus}
          isCeo={ARE_YOY_CEO}
        />
      ),
    },

    actionOwn: {
      title: '',
      key: 'action',
      width: 194,
      render: (_: void, record: BudgetRequest) => (
        <RequestOwnActions
          record={record}
          currentPeriodStatus={currentPeriodStatus}
        />
      ),
    },

    actionOwnReject: {
      title: '',
      key: 'action',
      width: 194,
      render: (_: void, record: BudgetRequest) => (
        <Flex justify="flex-end" gap="large" style={{ fontSize: 20 }}>
          <Tooltip title="Просмотр заявки">
            <EyeOutlined
              onClick={() =>
                openModalRequestView({
                  items: [record],
                  mode: 'own',
                  periodStatus: currentPeriodStatus,
                })
              }
              style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
            />
          </Tooltip>
          {isNotClosedPeriodStatus && (
            <Tooltip title="Редактировать заявку">
              <EditOutlined
                onClick={() =>
                  navigate(path.budget.budgets.edit(String(record.id)))
                }
                style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },

    actionOwnApprove: {
      title: '',
      key: 'action',
      width: 194,
      render: (_: void, record: BudgetRequest) => (
        <Flex justify="flex-end" gap="large" style={{ fontSize: 20 }}>
          <Tooltip title="Просмотр заявки">
            <EyeOutlined
              onClick={() =>
                openModalRequestView({
                  items: [record],
                  mode: 'own',
                  periodStatus: currentPeriodStatus,
                })
              }
              style={{ ...iconButtonStyle, color: 'var(--color-gray-60)' }}
            />
          </Tooltip>
          {canCreatePaymentRequest(record) && (
            <Tooltip title={`Создать заявку на оплату`}>
              <PayCircleOutlined
                onClick={() =>
                  navigate(
                    path.budget.budgets.createPaymentRequest(String(record.id)),
                  )
                }
              />
            </Tooltip>
          )}
        </Flex>
      ),
    },
  }

  const selectionSpacer = {
    title: '',
    key: 'selectionSpacer',
    width: 48,
  }

  return [
    ...(hasRowSelection || isApprovalList ? [] : [selectionSpacer]),
    ...columns.map((key) => allColumns[key]),
  ]
}
